import ErrorBoundary from "components/ErrorBoundary";
import LoadingWrapper from "./LoadingWrapper";
import { useTranslation } from "react-i18next";
import { HiOutlineChartBar } from "react-icons/hi";
import PropTypes from 'prop-types';

const CHART_BAR_HEIGHTS = [50, 70, 40, 85, 60, 75, 45, 90, 55, 65, 80, 50];
const CHART_BAR_IDS = CHART_BAR_HEIGHTS.map((h, i) => `chart-bar-${h}-${i}`);
const CARD_IDS = Array.from({ length: 8 }, (_, i) => `skeleton-card-${i}`);
const TABLE_COLS = [
    { id: 'col-0', w: 'w-1/3' },
    { id: 'col-1', w: 'w-1/4' },
    { id: 'col-2', w: 'w-1/4' },
    { id: 'col-3', w: 'w-1/6' },
];
const TABLE_ROW_IDS = Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`);

const ChartSkeleton = () => (
    <div className="animate-pulse px-2 pt-4">
        <div className="flex items-end gap-2 h-48">
            {CHART_BAR_HEIGHTS.map((h, i) => (
                <div key={CHART_BAR_IDS[i]} className="flex-1 bg-gray-200 rounded-t" style={{ height: `${h}%` }} />
            ))}
        </div>
        <div className="h-3 bg-gray-200 rounded mt-3 w-1/3 mx-auto" />
    </div>
);

const CardsSkeleton = () => (
    <div className="animate-pulse grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
        {CARD_IDS.map((id) => (
            <div key={id} className="bg-gray-100 rounded p-4 h-24 flex flex-col gap-2">
                <div className="h-3 bg-gray-200 rounded w-3/4" />
                <div className="h-6 bg-gray-200 rounded w-1/2 mt-auto" />
            </div>
        ))}
    </div>
);

const TableSkeleton = () => (
    <div className="animate-pulse space-y-3 py-4 px-2">
        <div className="flex gap-4 border-b pb-3">
            {TABLE_COLS.map(({ id, w }) => (
                <div key={id} className={`h-3 bg-gray-300 rounded ${w}`} />
            ))}
        </div>
        {TABLE_ROW_IDS.map((rowId) => (
            <div key={rowId} className="flex gap-4">
                {TABLE_COLS.map(({ id, w }) => (
                    <div key={`${rowId}-${id}`} className={`h-3 bg-gray-200 rounded ${w}`} />
                ))}
            </div>
        ))}
    </div>
);

const ChartEmpty = () => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
            <HiOutlineChartBar className="text-5xl" />
            <p className="text-sm font-medium">{t('table.no_chart_data')}</p>
            <p className="text-xs">{t('table.no_chart_data_description')}</p>
        </div>
    );
};

const LayoutWrapper = ({ loading = false, error = null, children = null, variant = 'default', isEmpty = false, className = '' }) => {
    let content;
    if (loading) {
        if (variant === 'chart') content = <ChartSkeleton />;
        else if (variant === 'cards') content = <CardsSkeleton />;
        else if (variant === 'table') content = <TableSkeleton />;
        else content = <LoadingWrapper loading={true} />;
    } else if (error) {
        content = <ErrorBoundary className="w-auto m-auto p-8" error={error} />;
    } else if (isEmpty) {
        content = <ChartEmpty />;
    } else {
        content = children;
    }
    return <div className={className}>{content}</div>;
}

LayoutWrapper.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.any,
  children: PropTypes.node,
  variant: PropTypes.string,
  isEmpty: PropTypes.bool,
  className: PropTypes.string,
};

export default LayoutWrapper;
